// File upload utility for web mode
// Uploads files to the server and returns file paths

import { getApiUrl } from './endpoints';

export interface UploadedFile {
  name: string;
  mimeType: string;
  filePath: string;
  size: number;
}

export interface UploadResult {
  files: UploadedFile[];
  rejected?: string[];
}

const ALLOWED_MIME_PREFIXES = [
  'image/', 'audio/', 'video/',
  'text/', 'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel', 'application/msword',
  'application/json', 'application/xml',
];

export const ALLOWED_EXTENSIONS = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.ico',
  '.mp3', '.wav', '.ogg', '.m4a', '.webm', '.flac', '.aac', '.wma',
  '.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv',
  '.txt', '.md', '.csv', '.tsv', '.log', '.rtf',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.json', '.xml', '.yaml', '.yml',
  '.drawio', '.vsdx', '.vsd',
]);

export function isAllowedFile(file: File): boolean {
  const ext = file.name.includes('.')
    ? '.' + file.name.split('.').pop()!.toLowerCase()
    : '';
  const mimeOk = ALLOWED_MIME_PREFIXES.some(prefix => file.type.startsWith(prefix));
  const extOk = ALLOWED_EXTENSIONS.has(ext);
  return mimeOk || extOk;
}

function getServerUrl(): string | null {
  // Shared with the rest of the app: the cowork/mobile client can be pointed at another host through
  // local storage, and an upload has to follow the same rule as every other API call.
  return getApiUrl();
}

function getAuthToken(): string | null {
  return localStorage.getItem('auth_token') || null;
}

export async function uploadFiles(files: File[]): Promise<UploadResult> {
  return uploadFilesWeb(files);
}



async function uploadFilesWeb(files: File[]): Promise<UploadResult> {
  const rejected: string[] = [];
  const accepted: File[] = [];
  for (const file of files) {
    if (!isAllowedFile(file)) {
      rejected.push(`${file.name}: unsupported file type`);
      continue;
    }
    accepted.push(file);
  }
  if (accepted.length === 0) {
    return { files: [], rejected };
  }

  const serverUrl = getServerUrl();
  if (!serverUrl) {
    throw new Error('No server URL configured for file upload');
  }

  const formData = new FormData();
  for (const file of accepted) {
    formData.append('files', file);
  }

  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${serverUrl}/api/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `Upload failed: ${response.status}` }));
    throw new Error(errorData.error || `Upload failed: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Upload failed');
  }

  const serverRejected = data.rejected as string[] | undefined;
  const allRejected = [...rejected, ...(serverRejected || [])];

  return { files: data.files as UploadedFile[], rejected: allRejected.length > 0 ? allRejected : undefined };
}

export async function uploadBlob(blob: Blob, name: string, mimeType: string): Promise<UploadedFile> {
  return uploadBlobWeb(blob, name, mimeType);
}


async function uploadBlobWeb(blob: Blob, name: string, mimeType: string): Promise<UploadedFile> {
  const serverUrl = getServerUrl();
  if (!serverUrl) {
    throw new Error('No server URL configured for file upload');
  }

  const file = new File([blob], name, { type: mimeType });
  const formData = new FormData();
  formData.append('files', file);

  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${serverUrl}/api/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `Upload failed: ${response.status}` }));
    throw new Error(errorData.error || `Upload failed: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Upload failed');
  }

  return data.files[0] as UploadedFile;
}

/**
 * Where a stored file is fetched from: always the page's own origin.
 *
 * Every row in `cat_photos` (and the profile pictures) is a path on the server that holds the database, and
 * `/api/files/:filename` only ever reads that server's upload directory — so the picture is always next to
 * the page that reported it. A configured server URL is how the cowork/mobile client reaches a *different*
 * host; for a page served from the app itself it could only point somewhere the file is not.
 */
export function getFileUrl(filePath: string): string | null {
  const filename = filePath.split(/[/\\]/).pop();
  if (!filename) return null;
  return `${window.location.origin}/api/files/${filename}`;
}

/**
 * A file URL an `<img src>` can open on its own.
 *
 * `/api/files/:filename` needs auth, and an image tag cannot send an Authorization
 * header — the two ways out are fetching the bytes and turning them into a data URL
 * (what `getFileDataUrl` does: no browser cache, +33% memory) or putting the token in
 * the query, which `authMiddleware` already accepts. This is the second one, so the
 * browser caches the picture like any other image.
 *
 * The access token is short-lived and appears in the URL, so it reaches the server's
 * access log; that is the trade-off, and why this is used for images rather than for
 * API calls.
 */
export function getAuthFileUrl(filePath: string): string | null {
  const url = getFileUrl(filePath);
  if (!url) return null;
  const token = getAuthToken();
  if (!token) return url;
  return `${url}?token=${encodeURIComponent(token)}`;
}

export async function getFileDataUrl(filePath: string): Promise<string | null> {
  const url = getFileUrl(filePath);
  if (!url) return null;
  try {
    const headers: Record<string, string> = {};
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) return null;
    const blob = await response.blob();
    return blobToDataUrlSync(blob);
  } catch {
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read blob'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

function blobToDataUrlSync(blob: Blob): Promise<string> {
  return blobToDataUrl(blob);
}